import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckCircle, Phone } from 'lucide-react';

export const CTASection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-24 relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-red-950"></div>
      
      {/* Grain Overlay */}
      <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)"/%3E%3C/svg%3E")'
      }}></div>

      {/* Sparkle Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-red-500 rounded-full"
            initial={{
              x: Math.random() * 100 + '%',
              y: Math.random() * 100 + '%',
              opacity: 0
            }}
            animate={{
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Individuelle Lösungen für
            <span className="block bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent mt-2">
              Ihr Dachprojekt
            </span>
          </h2>

          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Jedes Dach ist einzigartig. Wir entwickeln maßgeschneiderte Konzepte, die perfekt zu Ihren Anforderungen passen.
          </p>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Benefit text="Kostenlose Erstberatung" />
            <Benefit text="Unverbindliches Angebot" />
            <Benefit text="Persönliche Betreuung" />
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <motion.button
              onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
              className="relative group px-12 py-5 bg-gradient-to-r from-red-600 to-red-800 text-white text-lg font-bold rounded-lg overflow-hidden shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10">Kostenlose Beratung sichern</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-900"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>

            <motion.a
              href="tel:+4991198012850"
              className="flex items-center space-x-3 px-12 py-5 bg-white/10 backdrop-blur-md text-white text-lg font-bold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Phone size={24} />
              <span>0911 - 980 12 850</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Benefit = ({ text }) => (
  <motion.div
    className="flex items-center justify-center space-x-3 text-white"
    whileHover={{ scale: 1.05 }}
  >
    <CheckCircle className="text-red-500" size={28} />
    <span className="text-lg font-semibold">{text}</span>
  </motion.div>
);